import { formatQuantity } from "@/features/inventory/lib/quantity";

/**
 * Satış etiketleri ve rozet tonları. Hesap yok, yalnız isimlendirme.
 */

export const QUOTATION_STATUS_LABELS: Record<string, string> = {
  DRAFT: "Taslak",
  SENT: "Gönderildi",
  ACCEPTED: "Kabul edildi",
  REJECTED: "Reddedildi",
  EXPIRED: "Süresi doldu",
  CANCELLED: "İptal",
};

export const ORDER_STATUS_LABELS: Record<string, string> = {
  DRAFT: "Taslak",
  CONFIRMED: "Onaylandı",
  // satis-duzeltme 20 Ekim 2026 (S-1): sipariş sevk belgesiyle bu iki duruma geçiyor.
  PARTIALLY_SHIPPED: "Kısmen sevk edildi",
  SHIPPED: "Sevk edildi",
  INVOICED: "Faturalandı",
  CANCELLED: "İptal",
};

/**
 * Fiyatın NEREDEN geldiği. Satıcı "bu fiyat neden böyle?" sorusunu cevaplayamıyorsa fiyat
 * listesi kullanılmaz hâle gelir ve herkes elle yazmaya başlar — bu üç etiket o soruyu
 * cevaplıyor.
 */
export const PRICE_SOURCE_LABELS: Record<string, string> = {
  PARTNER: "Müşteriye özel liste",
  DEFAULT: "Varsayılan liste",
  MANUAL: "Elle girildi",
};

type Tone = "neutral" | "success" | "warning" | "danger" | "info";

export const QUOTATION_TONE: Record<string, Tone> = {
  DRAFT: "neutral",
  SENT: "info",
  ACCEPTED: "success",
  REJECTED: "danger",
  // Süresi dolmuş bir teklif bir HATA değil ama bir eylem gerektiriyor: ya yenilenecek ya
  // kapatılacak. Kırmızı değil sarı.
  EXPIRED: "warning",
  CANCELLED: "neutral",
};

export const ORDER_TONE: Record<string, Tone> = {
  DRAFT: "neutral",
  CONFIRMED: "info",
  PARTIALLY_SHIPPED: "warning",
  SHIPPED: "success",
  INVOICED: "success",
  CANCELLED: "danger",
};

/** Elle girilen fiyat DİKKAT ÇEKER: listeden sapma, görülmesi gereken bir karardır. */
export const PRICE_SOURCE_TONE: Record<string, Tone> = {
  PARTNER: "info",
  DEFAULT: "neutral",
  MANUAL: "warning",
};

export function labelOf(map: Record<string, string>, key: string): string {
  return map[key] ?? key;
}

/**
 * Bir satırın miktarı BİRİMİYLE: «120 kutu» (17 Ekim 2026).
 *
 * «Birimsiz miktar» bu satırın yokluğuydu: ekran `quantity(...)` ile yalnız sayıyı yazıp yanına
 * birimin KODUNU (`KUTU-DEMO2`) ekliyordu, ve satır birim göndermediği için o da boştu. Sunucu
 * artık satırın birimini (yoksa ürünün temel birimini) kod ve adıyla gönderiyor; kelime Stok
 * ekranlarıyla aynı kuraldan geçiyor (`formatQuantity`: ad, Türkçe küçük harf).
 */
export function lineQuantityText(line: {
  quantity: string;
  uomCode: string | null;
  uomName: string | null;
}): string {
  return formatQuantity(line.quantity, { code: line.uomCode, name: line.uomName });
}

/**
 * Önizlemenin miktarı altı ondalıkla geliyor («280.000000»): anlamsız sıfırlar okunmasın, kullanılan ondalık kalsın («12.5»). Yalnız dizge işi,
 * sayıya çevrilmiyor.
 */
export function wholeQuantity(value: string): string {
  return value.includes(".") ? value.replace(/\.?0+$/, "") : value;
}

/** Çekmecenin satır sorgusu: önceden alma aynı anahtarı doldursun diye tek yerde. */
export const ORDER_LINES_PAGE = { limit: 200, sortBy: "lineNo", sortOrder: "asc" } as const;

/** Belgenin aşamaları — teklif ve sipariş kartının başlığındaki salt okunur çubuk (KY-2). */
export const QUOTATION_STAGES = ["Taslak", "Gönderildi", "Kabul edildi", "Siparişe döndü"];
/**
 * Siparişin ERP'deki zinciri sevkte biter: fatura ve e-belgeler Logo'da kesilir (KARAR, 19 Ekim 2026).
 * «Faturalandı» aşaması bu yüzden çubukta yok — olsaydı sevk edilmiş sipariş «Aşama 4/4 · Faturalandı»
 * diye ERP'de hiç gelmeyecek bir adımı bekliyor görünürdü.
 */
export const ORDER_STAGES = ["Taslak", "Onaylandı", "Sevk edildi"];

/** Teklifin hangi aşamada olduğu; reddedilen, iptal edilen ve süresi dolan kapanmıştır. */
export function quotationStage(status: string, converted: boolean): number {
  if (converted) return 4;
  if (status === "ACCEPTED") return 2;
  if (status === "SENT") return 1;
  return 0;
}

export function orderStage(status: string): number {
  // Sevk edilen sipariş ERP'de tamamlanmıştır; INVOICED ileride Logo'dan dönerse de öyle.
  if (status === "INVOICED" || status === "SHIPPED") return ORDER_STAGES.length;
  // Kısmen sevk: onay geçti, sevk SÜRÜYOR — çubukta etkin aşama «Sevk edildi».
  if (status === "PARTIALLY_SHIPPED") return 2;
  if (status === "CONFIRMED") return 1;
  return 0;
}
